import { z } from "zod";

/**
 * Every code the API can return. The client renders t('errors:' + code).
 * The server never sends a localized or provider message. Job failures are
 * not error codes: they travel as Failure.key (an errors:jobs.* i18n key).
 *
 * UNSUPPORTED_FORMAT: the upload is neither DOCX, EPUB, nor Markdown (and not a PDF), or a cover is not an image.
 * BOOK_STATE_CONFLICT: the Book is not in a state that allows this call (e.g. language change after confirmation).
 * CHAPTER_STATE_CONFLICT: the Chapter is not in a state that allows this call (e.g. re-run while refining, excluded).
 * CHAPTER_NOT_APPROVED: a single-Chapter render on a Chapter without an Approved script.
 * RENDER_IN_PROGRESS: a Book render is already running.
 * RERENDER_NOT_CONFIRMED: the Chapter already has Chapter Audio and the re-render was not confirmed.
 * RENDER_SETTINGS_LOCKED: model or stability change after the first Chapter Audio exists.
 * JOB_NOT_RETRYABLE: the job is not parked for manual retry.
 * RESPEND_NOT_CONFIRMED: the parked job holds a paid attempt whose outcome nobody can
 *   recover, so retrying it may pay for the same audio twice; the retry has to say so.
 */
export const ERROR_CODES = [
  "VOICE_UNAVAILABLE",
  "VOICE_PRICING_UNKNOWN",
  "DRAFT_CONFLICT",
  "UNAPPLIED_CHANGES",
  "METADATA_CONFIRMATION_REQUIRED",
  "UNAUTHENTICATED",
  "INVALID_PASSWORD",
  "TOO_MANY_ATTEMPTS",
  "NOT_FOUND",
  "VALIDATION_FAILED",
  "PDF_NOT_SUPPORTED",
  "UNSUPPORTED_FORMAT",
  "BOOK_STATE_CONFLICT",
  "CHAPTER_STATE_CONFLICT",
  "CHAPTER_NOT_APPROVED",
  "RENDER_IN_PROGRESS",
  "RERENDER_NOT_CONFIRMED",
  "RENDER_SETTINGS_LOCKED",
  "JOB_NOT_RETRYABLE",
  "RESPEND_NOT_CONFIRMED",
  "STYLE_SHEET_CONFLICT",
  "CHAPTER_RENDERING",
  "DROPPED_NODES",
  "QUALITY_REVIEW_REQUIRED",
  "CASTING_LOCKED",
  "CASTING_MISSING",
  "VOICE_LANGUAGE_MISMATCH",
  "NOT_ALL_APPROVED",
  "ESTIMATE_STALE",
  "PAYLOAD_TOO_LARGE",
  "SERVICE_UNAVAILABLE",
  "INTERNAL",
] as const;
export const ErrorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

/** Interpolation values for the translated message, e.g. { retryAfterMinutes: 15 }. */
export const ErrorParamsSchema = z.record(
  z.string(),
  z.union([z.string(), z.number(), z.boolean()]),
);
export type ErrorParams = z.infer<typeof ErrorParamsSchema>;

export const ApiErrorSchema = z.strictObject({
  error: z.strictObject({
    code: ErrorCodeSchema,
    params: ErrorParamsSchema.optional(),
  }),
});
export type ApiError = z.infer<typeof ApiErrorSchema>;

export const HTTP_STATUS_BY_CODE: Readonly<Record<ErrorCode, number>> = Object.freeze({
  VOICE_UNAVAILABLE: 409,
  VOICE_PRICING_UNKNOWN: 409,
  DRAFT_CONFLICT: 409,
  UNAPPLIED_CHANGES: 409,
  METADATA_CONFIRMATION_REQUIRED: 409,
  UNAUTHENTICATED: 401,
  INVALID_PASSWORD: 401,
  TOO_MANY_ATTEMPTS: 429,
  NOT_FOUND: 404,
  VALIDATION_FAILED: 400,
  PDF_NOT_SUPPORTED: 415,
  UNSUPPORTED_FORMAT: 415,
  BOOK_STATE_CONFLICT: 409,
  CHAPTER_STATE_CONFLICT: 409,
  CHAPTER_NOT_APPROVED: 409,
  RENDER_IN_PROGRESS: 409,
  RERENDER_NOT_CONFIRMED: 409,
  RENDER_SETTINGS_LOCKED: 409,
  JOB_NOT_RETRYABLE: 409,
  RESPEND_NOT_CONFIRMED: 409,
  STYLE_SHEET_CONFLICT: 409,
  CHAPTER_RENDERING: 409,
  DROPPED_NODES: 409,
  QUALITY_REVIEW_REQUIRED: 409,
  CASTING_LOCKED: 409,
  CASTING_MISSING: 409,
  VOICE_LANGUAGE_MISMATCH: 422,
  NOT_ALL_APPROVED: 409,
  ESTIMATE_STALE: 409,
  PAYLOAD_TOO_LARGE: 413,
  SERVICE_UNAVAILABLE: 503,
  INTERNAL: 500,
});
